import React, { useCallback, useMemo } from 'react';
import { Card } from '../Card/Card';
import {
  GAME_INFO_CARD_TYPES,
  GameInfoCardType,
} from '@/utilities/constants/game-help';
import { useAppOrientation } from '@/lostHooks/orientation/useOrientation';
import useEventManagerEmitter from '@/event-manager/use-event-manager-emiter';
import {
  ANDROID_WRAPPER_EVENTS,
  GAME_WINDOW_TO_CLIENT_EVENTS,
} from '@/event-manager/types';
import { sendAndroidWrapperEvent } from '@/utilities/helpers/wrapperHelpers';
import { presentResponsiblePlaySettings } from '@/unified-modules/rg/rg-library';
import { PLATFORMS } from '@/utilities/constants/game';
import './styles/gameHelp.scss';
import { GameHelpItem } from '@/store/slices/commonUi/types';
import { useShallow } from 'zustand/react/shallow';
import { useStore } from '@/store/useStore';
import { setGameWindowContext } from '@/store/actions.ts';

export const RESPONSIBLE_GAMING_TITLE = 'responsible gaming';

export type CardActionMap = {
  [key in GameInfoCardType]: (item?: GameHelpItem) => void;
};

export const GameHelp: React.FC = () => {
  const orientation = useAppOrientation();
  const gameHelpItems = useStore(
    useShallow((s) => s.commonUIContext.gameHelpItems),
  );
  const { emitClientEvent } = useEventManagerEmitter();
  const platform = useStore((s) => s.runtime.platform);
  const wrapperType = useStore(useShallow((s) => s.runtime.wrapperType));

  const cardActionMapper: CardActionMap = useMemo(
    () => ({
      [GAME_INFO_CARD_TYPES.HOW_TO_PLAY]: () => {
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_RESUME);
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.TOGGLE_HELP);
        setGameWindowContext({ gameWindowOpen: false });
      },
      [GAME_INFO_CARD_TYPES.PAYTABLE]: () => {
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.AUTO_SPIN_RESUME);
        emitClientEvent(GAME_WINDOW_TO_CLIENT_EVENTS.TOGGLE_PAYTABLE);
        setGameWindowContext({ gameWindowOpen: false });
      },
    }),
    [emitClientEvent],
  );

  const handleCard = useCallback(
    (item: GameHelpItem, e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      const cardTitle = item.card_title?.[0]?.text?.toLowerCase();

      // Responsible Gaming on desktop opens the settings modal, not a link.
      if (
        cardTitle === RESPONSIBLE_GAMING_TITLE &&
        (platform === PLATFORMS.Desktop || platform === PLATFORMS.MobileWeb)
      ) {
        e.preventDefault();
        void presentResponsiblePlaySettings();
        return;
      }

      const url = item.card_link?.url;
      if (!url) {
        return;
      }

      if (wrapperType.isXsell) {
        e.preventDefault();
        sendAndroidWrapperEvent(
          ANDROID_WRAPPER_EVENTS.OPEN_EXTERNAL_WEBVIEW,
          url,
        );
        return;
      }
    },
    [platform, wrapperType],
  );

  if (!gameHelpItems || gameHelpItems.length === 0) {
    return null;
  }

  return (
    <div className={`game-help-container ${orientation || 'desktop'}`}>
      {gameHelpItems.map((item, index) => {
        if (item.show_card && item.card_title?.[0]?.text) {
          return (
            <Card
              key={index}
              title={item.card_title[0].text}
              action={(e) => {
                const cardType = item.card_type as GameInfoCardType;
                if (cardType && cardActionMapper[cardType]) {
                  cardActionMapper[cardType](item);
                }
                handleCard(item, e);
              }}
              url={item.card_link?.url}
            />
          );
        }
        return null;
      })}
    </div>
  );
};
